// The Composer's left STEP RAIL (prototype wk-steprail): the 8 build steps as freely-navigable
// buttons, each marked done/current/todo, with the activation gate step carrying the shield mark in
// the amber gate tone. The rail is NAVIGATION ONLY — it decides nothing; the step content and every
// configuration value render from the projection elsewhere (G-182). Tokens only (G-181); machine
// state rides data-step/data-state so tests assert behaviour, not copy (G-180). The step marks are
// round status dots (the governed radius-full exception).

import { Icon } from '../../../../app/Icon.tsx';
import { COMPOSER_STEPS, type ComposerStep } from '../composer-model.ts';
import { ShieldIcon } from '../composer-ui.tsx';

type RailState = 'cur' | 'done' | 'todo';

const MARK_CLASS: Readonly<Record<RailState, string>> = {
  cur: 'border-action bg-action text-action-text',
  done: 'border-sage bg-forest-tint text-forest',
  todo: 'border-border bg-surface text-text-subtle',
};

const GATE_MARK_CLASS: Readonly<Record<RailState, string>> = {
  cur: 'border-bracken bg-bracken text-action-text',
  done: 'border-sage bg-forest-tint text-forest',
  todo: 'border-bracken bg-bracken-tint text-bracken',
};

const TITLE_CLASS: Readonly<Record<RailState, string>> = {
  cur: 'font-semibold text-forest',
  done: 'text-text',
  todo: 'text-text-muted',
};

function railState(index: number, current: number, maxReached: number): RailState {
  if (index === current) return 'cur';
  return index < maxReached ? 'done' : 'todo';
}

function RailMark({
  step,
  state,
}: {
  readonly step: ComposerStep;
  readonly state: RailState;
}): React.JSX.Element {
  const tone = step.gate ? GATE_MARK_CLASS[state] : MARK_CLASS[state];
  return (
    <span
      className={`ritona-numeric flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs ${tone}`}
    >
      {step.gate ? (
        <ShieldIcon size={12} />
      ) : state === 'done' ? (
        <Icon name="check" size={12} />
      ) : (
        step.number
      )}
    </span>
  );
}

export interface StepRailProps {
  readonly workflowName: string;
  readonly currentIndex: number;
  readonly maxReached: number;
  readonly onSelect: (index: number) => void;
}

/** The compose-step rail. Any step can be opened directly (prototype data-cmp-step). */
export function StepRail({
  workflowName,
  currentIndex,
  maxReached,
  onSelect,
}: StepRailProps): React.JSX.Element {
  return (
    <nav
      aria-label="Compose steps"
      className="lg:sticky lg:top-6 lg:self-start lg:border-r lg:border-border lg:pr-6"
    >
      <div className="ritona-numeric mb-4 text-xs uppercase tracking-eyebrow text-text-subtle">
        Build · {workflowName.toLowerCase()}
      </div>
      <ol className="flex flex-col gap-1">
        {COMPOSER_STEPS.map((step, index) => {
          const state = railState(index, currentIndex, maxReached);
          return (
            <li key={step.id}>
              <button
                type="button"
                data-step={step.id}
                data-state={state}
                aria-current={index === currentIndex ? 'step' : undefined}
                onClick={() => onSelect(index)}
                className="flex w-full items-start gap-3 py-1.5 text-left"
              >
                <RailMark step={step} state={state} />
                <span className="flex min-w-0 flex-col gap-0.5 pt-0.5">
                  <span className={`text-sm ${TITLE_CLASS[state]}`}>{step.railTitle}</span>
                  <span className="ritona-numeric text-xs text-text-subtle">{step.railSub}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
